import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { sanitize } from 'utils/validation';
import { analyticsInteraction } from 'utils/analytics';
import { getFormattedPrice } from 'utils/prices';
import { announceAssistiveStatus } from 'utils/accessibility/assistiveStatusAnnouncer';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { ANALYTICS, RESERVATIONS, GLOBAL, TOOLTIP_THEMES, COMPONENTS } from 'constants';
import IconAlert from 'components/IconAlert';
import FormattedPrice from 'components/FormattedPrice';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Button from 'components/Button';

const { EXTRAS_INCLUDED_STATUS, EXTRAS_MANDATORY_STATUS, EXTRAS_WAIVED_STATUS } = RESERVATIONS;
const { LIMITED_INVENTORY, RECOMMENDED } = COMPONENTS.INDIVIDUAL_EXTRA_ALERT;

/**
 * Extra:
 * This is the header on the extras page
 *
 * @param {object}  props - React Props
 * @param {func}    props.extrasUpdate - we use this to update extras
 * @param {func}    props.showExclusionsModal - we use this to show the exclusions modal
 * @param {string}  props.exclusionsDescription - the exclusions description
 * @param {boolean} props.isBundleIncluded - is the buddle included
 * @param {object}  props.breakpoint - breakpoint flags
 * @param {string}  props.exclusionsText - exclusions text
 * @param {object}  props.extra - the extra object
 * @param {bool}    props.hasWarnedAboutLimitedInventoryItem - if the user has already been warned they are adding a limited inventory item
 * @param {bool}    props.isCheckIn - if this is showing in OCI, use different DTM Track attributes
 * @param {bool}    props.isCustomPathCheckIn - if this is showing in Custom Path OCI, use different DTM Track attributes
 * @param {func}    props.setHasWarnedAboutLimitedInventoryItemToTrue - function to set the hasWarnedAboutLimitedInventoryItem value
 * @param {func}    props.openLimitedInventoryModal - opens the limited inventory warning modal
 * @param {object}  props.hashTableOfRecommendedExtras - hash table with the extras code as keys
 * @param {bool}  props.showShortDescription - show extra short description and description only for trip enhancements
 * @param {bool}  props.isADAEquipment - is ADA equipment
 * @return {JSX}
 */

const Extra = ({
  extrasUpdate,
  showExclusionsModal,
  exclusions,
  isBundleIncluded,
  breakpoint,
  extra,
  hasWarnedAboutLimitedInventoryItem,
  hasWarnedAdaptativeEquipementItem,
  isCheckIn,
  isCustomPathCheckIn,
  setHasWarnedAboutLimitedInventoryItemToTrue,
  setHasWarnedAboutAdaptativeEquipementToTrue,
  openLimitedInventoryModal,
  openLimitedAdaptiveEquipmentModal,
  hashTableOfRecommendedExtras,
  selectedMoreThanBundled,
  toggleExtrasLoading,
  showShortDescription,
  isADAEquipment,
  reservationLimitedAdvanceNoticeIndicator,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldShowLimitedInventoryModal, setShouldShowLimitedInventoryModal] = useState(false);
  const [shouldShowAdaptiveEquimentModal, setShouldShowAdaptiveEquimentModal] = useState(false);

  const getBusinessLogic = useCallback(() => {
    const { max_quantity, selected_quantity = 0, status, rate_amount_view } = extra;
    const hasCost = rate_amount_view && parseFloat(rate_amount_view?.amount) > 0;
    const isIncluded = status === EXTRAS_INCLUDED_STATUS;
    const isMandatory = status === EXTRAS_MANDATORY_STATUS;
    const isBuiltIn = isIncluded || isMandatory; // covers any status that defaults to a selected_quantity > 0.
    const minQuantity = isBuiltIn || isBundleIncluded ? 1 : 0; // assumes that included (bundles or otherwise) must default to 1

    return {
      hasCost,
      isIncluded,
      isMandatory,
      isBuiltIn,
      maxQuantity: max_quantity,
      minQuantity,
      selected_quantity,
    };
  }, [extra, isBundleIncluded]);

  const initialBusinessLogic = getBusinessLogic();

  const [businessLogic, setBusinessLogic] = useState(initialBusinessLogic);
  const [selected_quantity, setSelectedQuantity] = useState(initialBusinessLogic.selected_quantity);

  useEffect(() => {
    const initialLogic = getBusinessLogic();

    setBusinessLogic({
      hasCost: initialLogic.hasCost,
      isIncluded: initialLogic.isIncluded,
      isMandatory: initialLogic.isMandatory,
      isBuiltIn: initialLogic.isBuiltIn,
      maxQuantity: initialLogic.maxQuantity,
      minQuantity: initialLogic.minQuantity,
    });

    setSelectedQuantity(extra.selected_quantity ?? 0);

    if (extra.selected_quantity || extra.selected_quantity === 0) {
      setSelectedQuantity(extra.selected_quantity);
    } else if (setHasWarnedAboutAdaptativeEquipementToTrue && !extra.selected_quantity) {
      setHasWarnedAboutAdaptativeEquipementToTrue(false);
      setHasWarnedAboutLimitedInventoryItemToTrue(false);
    }
  }, [
    extra.selected_quantity,
    isBundleIncluded,
    hasWarnedAdaptativeEquipementItem,
    hasWarnedAboutLimitedInventoryItem,
  ]);

  const handleAcceptForLimitedInventoryModal = () => {
    //  What we pass to the openLimitedInventoryModal modal to update the item + close modal

    //  hasWarnedAboutLimitedInventoryItem in the parent component is the flag to tell us if we have already have shown the
    //  the limited inventory modal, this only happens when the user clicks add.  Since setState is async we need to make sure that is complete before opening the modal
    //  otherwise we have the edge case of when a _quick_ user opening the modal, closes it and opens it again when really we should
    // just add the extra w/o showing the modal.
    //  So we set shouldShowLimitedInventoryModal to true in internal, then when hasWarnedAboutLimitedInventoryItem changes from false to true AND
    // shouldShowLimitedInventoryModal is true we know this individual extra should pop the modal.  And we know it won't pop again.

    setShouldShowLimitedInventoryModal(true);
    setHasWarnedAboutLimitedInventoryItemToTrue();
  };

  const handleAcceptForAdaptativeEquipementModal = () => {
    setShouldShowAdaptiveEquimentModal(true);
    setHasWarnedAboutAdaptativeEquipementToTrue();
  };

  const iconUri = useMemo(() => {
    let uri = extra?.extras_icon_url || extra?.icon_url;
    const selected = selected_quantity > 0;
    if (typeof uri !== 'string') return null;

    if (selected) {
      const parts = uri.split('/');

      const fileNameParts = parts.pop().split('.');
      const fileNameSelected = fileNameParts.join('-selected.');

      uri = [...parts, fileNameSelected].join('/');
    }

    return uri;
  }, [selected_quantity, extra]);

  const debouncedUpdate = utils.debounce((quantity) => {
    toggleExtrasLoading?.(true);
    setIsUpdating(true);
    extrasUpdate(extra, quantity).then(() => {
      toggleExtrasLoading?.(false);
      setIsUpdating(false);
    });
  }, 600);

  const handleIncrementExtra = () => {
    // Updates live region text for screen readers when adding extra
    announceAssistiveStatus(
      `${extra.name} ${i18n('extras_added')}. ${i18n('common_quantity')} ${selected_quantity + 1}`
    );
    const isLimitedInventoryItem = GLOBAL.LIMITED_INVENTORY_EXTRA_CODE === extra?.allocation;
    if (!hasWarnedAdaptativeEquipementItem && extra.adaptive_equipment && !selected_quantity) {
      openLimitedAdaptiveEquipmentModal(handleAcceptForAdaptativeEquipementModal, extra?.code);
      const modalAnalyticsKey = reservationLimitedAdvanceNoticeIndicator
        ? ANALYTICS.LIMITED_TIME_ADAPTIVE_DRIVING_DEVICE
        : ANALYTICS.ADAPTIVE_DRIVING_DEVICE;
      analyticsInteraction(ANALYTICS.MODAL, modalAnalyticsKey, ANALYTICS.OPEN);
    } else if (
      !hasWarnedAboutLimitedInventoryItem &&
      isLimitedInventoryItem &&
      !extra.adaptive_equipment &&
      !selected_quantity
    ) {
      openLimitedInventoryModal(handleAcceptForLimitedInventoryModal, extra?.code);
    } else {
      setSelectedQuantity((prev) => {
        const updatedQuantity = prev + 1;
        debouncedUpdate(updatedQuantity);
        return updatedQuantity;
      });
    }
  };

  const handleRemoveExtra = () => {
    // Updates live region text for screen readers when removing extra
    announceAssistiveStatus(
      `${extra.name} ${i18n('extras_remove')}. ${i18n('common_quantity')} ${selected_quantity - 1}`
    );
    setSelectedQuantity(0);
    toggleExtrasLoading?.(true);
    setIsUpdating(true);
    extrasUpdate(extra, 0).then(() => {
      toggleExtrasLoading?.(false);
      setIsUpdating(false);
    });
  };

  useEffect(() => {
    if (hasWarnedAboutLimitedInventoryItem && shouldShowLimitedInventoryModal) {
      handleIncrementExtra();
      setShouldShowLimitedInventoryModal(false);
    } else if (hasWarnedAdaptativeEquipementItem && shouldShowAdaptiveEquimentModal) {
      handleIncrementExtra();
      setShouldShowAdaptiveEquimentModal(false);
    }
  }, [shouldShowLimitedInventoryModal, shouldShowAdaptiveEquimentModal]);

  const getDtmTrack = (type, code) => {
    if (isCheckIn) {
      return `check_in|extras_${type}|${code}`;
    }
    if (isCustomPathCheckIn) {
      return `custom_path_check_in|extras_${type}|${code}`;
    }
    return `extras|extras_${type}|${code}`;
  };

  const getRenderLogic = () => {
    const { code, status, total_amount_view, rate_amount_view, rate_type } = extra;
    const { isMobile } = breakpoint;

    const isFeeWaived = status === EXTRAS_WAIVED_STATUS;
    const disableSubtract = selected_quantity <= businessLogic.minQuantity;
    const disableAdd = selected_quantity >= businessLogic.maxQuantity;
    const isSelected = selected_quantity > 0;
    const showRemoveButton =
      !businessLogic.isBuiltIn && selected_quantity === businessLogic.maxQuantity && businessLogic.maxQuantity === 1;
    const showButtons = !showRemoveButton && isSelected && businessLogic.maxQuantity !== businessLogic.minQuantity;

    const showStatusTop = !isMobile || showButtons;

    const addedText =
      (isBundleIncluded &&
        i18n(businessLogic.maxQuantity === 1 ? 'extras_in_bundle' : 'extras_in_bundle_amount', [
          businessLogic.minQuantity,
        ])) ||
      i18n(`extras_status_${status}`);
    let priceDisplay = null;
    let incrementalPriceDisplay = null;
    let renderedStatus = null;
    if (!isSelected) {
      renderedStatus = (
        <Button
          button={false}
          className='individual-extra__add individual-extra__single-add'
          data-dtm-track={getDtmTrack('add', code)}
          disabled={isUpdating}
          loading={isUpdating}
          onClick={handleIncrementExtra}
          title=''
          ariaText={i18n('rental_details_add_aria_label', [extra.name])}
        >
          {i18n('extras_add')}
        </Button>
      );
    } else if (businessLogic.isBuiltIn) {
      renderedStatus = <span className='individual-extra__added-text'>{addedText}</span>;
    } else if (isSelected) {
      renderedStatus = showRemoveButton && (
        <Button
          className='individual-extra__sub individual-extra__single-remove'
          data-dtm-track={getDtmTrack('remove', code)}
          disabled={isUpdating}
          loading={isUpdating}
          link
          onClick={handleRemoveExtra}
          title=''
          ariaText={`${i18n('extras_remove')} ${extra?.name}`}
        >
          {i18n('extras_remove')}
        </Button>
      );
    }

    if (businessLogic.hasCost) {
      priceDisplay = getFormattedPrice(total_amount_view);

      if (config.getIsIncrementalPricingEnabled()) {
        incrementalPriceDisplay = `${getFormattedPrice(rate_amount_view)}/${i18n(`extras_rate_unit_${rate_type}`)}`;
        priceDisplay = isMobile
          ? `(${getFormattedPrice(total_amount_view)} ${i18n('vehicle_select_price_total').toLowerCase()})`
          : priceDisplay;
      }
    } else if (isFeeWaived) {
      priceDisplay = i18n('extras_fee_waived');
    }

    return {
      showStatusTop,
      disableAdd,
      disableSubtract,
      isSelected,
      priceDisplay,
      incrementalPriceDisplay,
      renderedStatus,
      showButtons,
    };
  };

  const handleExclusionsModal = () => {
    const { exclusionsDescription, exclusionsText } = exclusions;
    showExclusionsModal(exclusionsDescription, exclusionsText);
  };

  const handleDecrementExtra = () => {
    // Updates live region text for screen readers when removing extra
    announceAssistiveStatus(
      `${extra.name} ${i18n('extras_remove')}. ${i18n('common_quantity')} ${selected_quantity - 1}`
    );
    setSelectedQuantity((prev) => {
      const updatedQuantity = prev - 1;
      debouncedUpdate(updatedQuantity);
      return updatedQuantity;
    });
  };

  const handleToggleExpandDetails = () => {
    const { name } = extra;
    setExpanded(!expanded);

    // Fire off analytics "interaction" event for drawer
    analyticsInteraction(ANALYTICS.DRAWER, name, expanded ? ANALYTICS.CLOSE : ANALYTICS.OPEN);
  };

  const { code, detailed_description, description, name = '' } = extra;

  const {
    disableAdd,
    disableSubtract,
    isSelected,
    priceDisplay,
    incrementalPriceDisplay,
    renderedStatus,
    showButtons,
    showStatusTop,
  } = getRenderLogic();

  const isLimitedInventoryItem = GLOBAL.LIMITED_INVENTORY_EXTRA_CODE === extra.allocation;
  const shouldDisplayPrice = (!businessLogic.isIncluded || selectedMoreThanBundled) && !!priceDisplay;

  const sectionClasses = cn('individual-extra', {
    expanded,
    selected: isSelected,
    'component-theme--light-blue': isSelected,
    'component-theme--light': !isSelected,
  });

  return (
    <section className={sectionClasses}>
      <section className='individual-extra__container'>
        {showShortDescription && !!iconUri && (
          <img className='individual-extra__description-image' src={iconUri} alt='' />
        )}
      </section>
      <section className='individual-extra__container-details'>
        {isLimitedInventoryItem && !isADAEquipment && (
          <IconAlert
            typeOfAlert={LIMITED_INVENTORY}
            placementOfToolTip='top-start'
            additionalClass={cn({
              'icon-alert__not-selected-extra': !isSelected,
            })}
            toolTipTheme={cn({ [TOOLTIP_THEMES.LIGHT_BLUE]: !isSelected, [TOOLTIP_THEMES.REGULAR_SLATE]: !isSelected })}
          />
        )}
        {/* If the code is a recommended extra, put alert */}
        {hashTableOfRecommendedExtras[code] && (
          <IconAlert
            typeOfAlert={RECOMMENDED}
            placementOfToolTip='top-start'
            additionalClass={cn({
              'icon-alert__not-selected-extra': !isSelected,
            })}
            toolTipTheme={cn({ [TOOLTIP_THEMES.LIGHT_BLUE]: isSelected, [TOOLTIP_THEMES.REGULAR_SLATE]: !isSelected })}
          />
        )}
        <div className='individual-extra__upper'>
          <div className='individual-extra__upper-left'>
            <p className='individual-extra__name'>{name}</p>
            {showShortDescription && (
              <p className='individual-extra__short-description' dangerouslySetInnerHTML={sanitize(description)} />
            )}
          </div>
          <div
            className={cn('individual-extra__price-status', {
              'individual-extra__price-status--included': businessLogic.isIncluded && !selectedMoreThanBundled,
            })}
          >
            {shouldDisplayPrice && (
              <>
                {incrementalPriceDisplay && (
                  <FormattedPrice classNames='individual-extra__incremental-price'>
                    {incrementalPriceDisplay}
                  </FormattedPrice>
                )}
                <FormattedPrice classNames='individual-extra__price'>{priceDisplay}</FormattedPrice>
              </>
            )}
            {showStatusTop && (
              <div
                className={cn('individual-extra__status', {
                  'individual-extra__status--included': businessLogic.isBuiltIn,
                })}
              >
                {renderedStatus}
              </div>
            )}
          </div>
        </div>

        <div className='individual-extra__lower'>
          <Button
            onClick={handleToggleExpandDetails}
            link
            className='individual-extra__details-toggle'
            ariaExpanded={expanded.toString()}
            data-track-dtm={getDtmTrack('detail', code)}
            ariaText={`${name} ${i18n('extras_expand_button')}`}
          >
            {i18n('extras_expand_button')}
          </Button>

          {showButtons && (
            <div className='individual-extra__buttons'>
              <div
                className={cn('individual-extra__actions', {
                  'individual-extra__actions--loading animated-loader__before': isUpdating,
                })}
              >
                <Button
                  className='individual-extra__sub'
                  data-dtm-track={getDtmTrack('minus', code)}
                  disabled={disableSubtract || isUpdating}
                  button={false}
                  onClick={handleDecrementExtra}
                  ariaText={i18n('extras_decrease_accessibility', [name])}
                />
                <span className='individual-extra__quantity'>{selected_quantity}</span>
                <Button
                  className='individual-extra__add'
                  data-dtm-track={getDtmTrack('plus', code)}
                  disabled={disableAdd || isUpdating}
                  button={false}
                  onClick={handleIncrementExtra}
                  ariaText={i18n('extras_increase_accessibility', [name])}
                />
              </div>
              <span className='individual-extra__how-many'>{i18n('extras_how_many')}</span>
            </div>
          )}
          {!showStatusTop && (
            <div
              className={cn('individual-extra__status', {
                'individual-extra__status--included': businessLogic.isBuiltIn,
              })}
            >
              {renderedStatus}
            </div>
          )}
        </div>

        {expanded && (
          <div className='individual-extra__details'>
            <div className='individual-extra__details-text' dangerouslySetInnerHTML={sanitize(detailed_description)} />

            {exclusions?.exclusionsDescription && (
              <Button
                link
                className='individual-extra__details__exclusion-button'
                onClick={handleExclusionsModal}
                button={false}
              >
                {i18n('view_exclusions')}
              </Button>
            )}
          </div>
        )}
      </section>
    </section>
  );
};

Extra.propTypes = {
  // Container props
  breakpoint: PropTypes.object,
  exclusions: PropTypes.object,
  isBundleIncluded: PropTypes.bool,
  selectedMoreThanBundled: PropTypes.bool,
  hashTableOfRecommendedExtras: PropTypes.object,
  extrasUpdate: PropTypes.func,
  showExclusionsModal: PropTypes.func,
  openLimitedInventoryModal: PropTypes.func,
  openLimitedAdaptiveEquipmentModal: PropTypes.func,

  // Parent props
  extra: PropTypes.shape({
    max_quantity: PropTypes.number,
    selected_quantity: PropTypes.number,
    status: PropTypes.string,
    rate_amount_payment: PropTypes.object,
    rate_amount_view: PropTypes.object,
  }),
  hasWarnedAboutLimitedInventoryItem: PropTypes.bool,
  hasWarnedAdaptativeEquipementItem: PropTypes.bool,
  setHasWarnedAboutLimitedInventoryItemToTrue: PropTypes.func,
  setHasWarnedAboutAdaptativeEquipementToTrue: PropTypes.func,
  toggleExtrasLoading: PropTypes.func,
  isCheckIn: PropTypes.bool,
  isCustomPathCheckIn: PropTypes.bool,
  showShortDescription: PropTypes.bool,
  isADAEquipment: PropTypes.bool,
};

export default Extra;
